class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.searchPopupSearults = this.querySelector(
      ".m-search-popup__result"
    );
    this.popularSearches = this.querySelector(
      "[data-popular-searches]"
    );
    this.searchCount = this.querySelector(
      "[data-search-count]"
    );

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;

    popularSearchItems.forEach((itm) => {
      itm.href = createSearchLink(itm.dataset.psQuery);
    });
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");

    if (form) {
      form.addEventListener(
        "submit",
        this.onFormSubmit.bind(this)
      );
    }

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    if (clear) {
      clear.addEventListener(
        "click",
        this.onClearSearch.bind(this)
      );
    }

    MinimogEvents.subscribe(
      MinimogTheme.pubSubEvents.closeSearchPopup,
      () => this.close(true)
    );
  }

  getQuery() {
    return this.input?.value.trim() || "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onClearSearch(event) {
    event.preventDefault();

    if (!this.input) return;

    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    /*
     * Cache anahtarını normalize ediyoruz.
     * replaceAll yerine regex kullanılması eski browser desteği açısından
     * daha güvenli.
     */
    const queryKey = searchTerm
      .trim()
      .replace(/\s+/g, "-")
      .toLowerCase();

    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag =
      this.dataset.searchByTag === "true";

    const searchByBody =
      this.dataset.searchByBody === "true";

    /*
     * Shopify Predictive Search:
     * Stokta olmayan ürünleri sonuçlardan tamamen çıkar.
     */
    const unavailableProductsOption = "hide";

    /*
     * Shopify'ın önerilen temel arama alanları.
     */
    let searchFields =
      "title,product_type,vendor,variants.sku,variants.title";

    /*
     * Önceki kodda bunlar ters bağlanmıştı.
     */
    if (searchByTag) {
      searchFields += ",tag";
    }

    if (searchByBody) {
      searchFields += ",body";
    }

    /*
     * Daha önce aynı sorgu geldiyse tekrar request atma.
     */
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    /*
     * resources[limit]=10
     *
     * Shopify Predictive Search API'nin desteklediği
     * maksimum değer 10.
     *
     * limit_scope=each sayesinde ürün, collection,
     * page vb. resource türleri limiti ayrı ayrı kullanır.
     *
     * unavailable_products=hide sayesinde stokta olmayan
     * ürünler Shopify tarafında sonuçlardan çıkarılır.
     */
    const searchURL =
      `${window.MinimogSettings.routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&resources[limit]=10` +
      `&resources[limit_scope]=each` +
      `&resources[options][unavailable_products]=${unavailableProductsOption}` +
      `&resources[options][fields]=${encodeURIComponent(searchFields)}` +
      `&section_id=predictive-search`;

    fetch(searchURL)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            `Predictive Search request failed: ${response.status}`
          );

          this.close();

          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(
          text,
          "text/html"
        );

        const section = parsedHTML.querySelector(
          "#shopify-section-predictive-search"
        );

        /*
         * Section Shopify tarafından dönmezse
         * doğrudan hata verip bozuk HTML basmasını engelle.
         */
        if (!section) {
          throw new Error(
            "Predictive search section bulunamadı."
          );
        }

        const resultsMarkup = section.innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.toggleSpinnerLoading(false);
        this.toggleClearSearch(true);

        this.close();

        console.error(
          "[PredictiveSearch]",
          error
        );
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute("results", true);

    const searchItemsWrapper = this.querySelector(
      "[data-search-items-wrapper]"
    );

    /*
     * Wrapper'ın gerçekten sonuç içerip içermediğini
     * kontrol ediyoruz.
     */
    const hasResults =
      searchItemsWrapper &&
      searchItemsWrapper.children.length > 0;

    this.renderSearchQueryAndMessage(
      Boolean(hasResults)
    );

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);

    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const {
      input,
      searchQuery,
      searchMessage,
      moreResultIcon,
    } = this.domNodes;

    if (!input || !searchQuery || !searchMessage) {
      return;
    }

    const query = input.value;
    const { resultsTitle } = searchMessage.dataset;

    searchQuery.textContent = query;

    this.predictiveSearchResults?.classList.remove(
      "m:hidden"
    );

    this.searchPopupSearults?.classList.remove(
      "m:hidden"
    );

    if (results) {
      searchMessage.textContent = resultsTitle;

      moreResultIcon?.classList.add("m:hidden");
    } else {
      searchMessage.textContent =
        searchMessage.dataset.resultsTitle;

      moreResultIcon?.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;

    if (submit) {
      submit.style.visibility = show
        ? "hidden"
        : "visible";
    }

    if (loading) {
      loading.style.visibility = show
        ? "visible"
        : "hidden";
    }
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;

    if (!clear) return;

    clear.style.visibility = show
      ? "visible"
      : "hidden";
  }

  open() {
    if (!this.searchPopupSearults || !this.input) {
      return;
    }

    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${
        this.resultsMaxHeight ||
        this.getResultsMaxHeight()
      }px`
    );

    this.setAttribute("open", true);

    this.input.setAttribute(
      "aria-expanded",
      "true"
    );

    this.isOpen = true;

    if (this.popularSearches) {
      this.popularSearches.classList.add(
        "m:hidden"
      );
    }

    if (this.searchCount) {
      this.searchCount.classList.remove(
        "m:hidden"
      );
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = "";
      }

      this.removeAttribute("results");

      this.toggleClearSearch(false);

      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");

    this.predictiveSearchResults?.classList.add(
      "m:hidden"
    );

    this.searchPopupSearults?.classList.add(
      "m:hidden"
    );

    if (this.popularSearches) {
      this.popularSearches.classList.remove(
        "m:hidden"
      );
    }

    if (this.searchCount) {
      this.searchCount.classList.add(
        "m:hidden"
      );
    }

    if (this.input) {
      this.input.setAttribute(
        "aria-expanded",
        "false"
      );
    }

    this.isOpen = false;
  }

  getResultsMaxHeight() {
    const predictiveSearch =
      document.querySelector(
        "predictive-search"
      );

    if (!predictiveSearch) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      predictiveSearch
        .getBoundingClientRect()
        .bottom;

    return this.resultsMaxHeight;
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define(
    "predictive-search",
    PredictiveSearch
  );
}